import type { DistrictNcesId } from "@nimble/datamodel/types/ncesId";
import queryString from "query-string";

import {
  ALERTS_DRAWER_OPEN_VALUE,
  ALERTS_DRAWER_PARAM,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/alerts-drawer";
import { FilterValues } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/filter-schema";
import {
  DISTRICT_JOB_DRAWER_PARAM,
  toDistrictJobDrawerValue,
} from "@/components/shared/jobs/district-job-details-drawer/district-job-drawer-param";
import { publicEnv } from "@/env/public-env";
import {
  JOBS_DESTINATION_PARAM,
  JobsDestination,
} from "@/lib/jobs-destination";
import { StateConfig } from "@/lib/states-config";
import { InviteTabValue } from "@/lib/types/invites.types";

/**
 * Get the base URL for the site
 */
export function getSiteUrl(): string {
  const origin = publicEnv.NEXT_PUBLIC_SITE_URL;

  // Ensure proper joining: remove trailing slash from origin, ensure basePath starts with /
  const cleanOrigin = origin.replace(/\/$/, "");

  return cleanOrigin;
}

function buildStateJobsUrl(stateConfig: StateConfig, pathname: string) {
  return `/jobs/${stateConfig.route}${pathname}`;
}

export const DEFAULT_INVITE_TAB: InviteTabValue = "invitations";

const ATS_CANDIDATE_DASHBOARD_PATH = "/candidate-dashboard";
const ATS_HOME_PATH = "/";
const DISCOVER_APPLICATION_SOURCE = "discover";

const routes = {
  getJobs: () => {
    return "/jobs";
  },
  getJobSearchOrigin: () => {
    return `/jobs?${JOBS_DESTINATION_PARAM}=${JobsDestination.Origin}`;
  },
  getJobsState: ({ state }: { state: StateConfig }) => {
    return buildStateJobsUrl(state, "");
  },
  getJobsStateWithFilters: ({
    state,
    filters,
    extraParams,
  }: {
    state: StateConfig;
    filters: FilterValues;
    extraParams?: Record<string, string>;
  }) => {
    const search = queryString.stringify(
      { ...filters, ...extraParams },
      { skipNull: true, skipEmptyString: true, arrayFormat: "bracket" },
    );
    const base = buildStateJobsUrl(state, "");
    return search ? `${base}?${search}` : base;
  },
  getJobsSaved: ({ state }: { state: StateConfig }) => {
    return buildStateJobsUrl(state, "/saved");
  },
  getJobsStateAlerts: ({ state }: { state: StateConfig }) => {
    return `${buildStateJobsUrl(state, "")}?${ALERTS_DRAWER_PARAM}=${ALERTS_DRAWER_OPEN_VALUE}`;
  },
  getDistrict: ({
    state,
    districtId,
  }: {
    state: StateConfig;
    districtId: DistrictNcesId;
  }) => {
    return buildStateJobsUrl(state, `/districts/${districtId}`);
  },
  getDistrictJob: ({
    state,
    districtId,
    jobId,
  }: {
    state: StateConfig;
    districtId: DistrictNcesId;
    jobId: string;
  }) => {
    const districtUrl = routes.getDistrict({ state, districtId });
    return `${districtUrl}?${DISTRICT_JOB_DRAWER_PARAM}=${toDistrictJobDrawerValue(districtId, jobId)}`;
  },
  getSchool: ({
    state,
    districtId,
    schoolId,
  }: {
    state: StateConfig;
    districtId: string;
    schoolId: string;
  }) => {
    return buildStateJobsUrl(
      state,
      `/districts/${districtId}/schools/${schoolId}`,
    );
  },
  getSchoolJobs: ({
    state,
    districtId,
    schoolId,
  }: {
    state: StateConfig;
    districtId: string;
    schoolId: string;
  }) => {
    return buildStateJobsUrl(
      state,
      `/districts/${districtId}/schools/${schoolId}`,
    );
  },
  getSchoolJob: ({
    state,
    districtId,
    schoolId,
    jobId,
  }: {
    state: StateConfig;
    districtId: string;
    schoolId: string;
    jobId: string;
  }) => {
    return buildStateJobsUrl(
      state,
      `/districts/${districtId}/schools/${schoolId}/job/${jobId}`,
    );
  },
  getSignIn: () => {
    return "/candidate/login";
  },
  getSignUp: () => {
    return "/candidate/signup";
  },
  getHireSignIn: () => {
    return "/hire/login";
  },
  getPasswordLogin: () => {
    return "/login/password";
  },
  getDistrictsMarketing: () => {
    return "https://www.hirenimble.com/districts";
  },
  getProfileInfo: () => {
    return "/candidate/profile";
  },
  getProfileQualifications: () => {
    return "/candidate/qualifications";
  },
  getProfilePreferences: () => {
    return "/candidate/preferences";
  },
  getProfileQuizIntro: () => {
    return "/candidate/quiz-intro";
  },
  getProfileQuiz: () => {
    return "/candidate/quiz";
  },
  getProfileQuizQuestions: () => {
    return "/candidate/quiz/questions";
  },
  getProfileQuizDefinitions: () => {
    return "/candidate/quiz/definitions";
  },
  getCandidateInvites: (tab: InviteTabValue) => {
    return `/candidate/invites/${tab}`;
  },
  getCandidateApplications: () => {
    return "/candidate/applications";
  },
  getCoreApplicationView: (jobId: string) => {
    return `/applicationconfirmation/?job_id=${jobId}&ea=t`;
  },
  getCandidateApplicationView: (applicationId: string) => {
    return `/candidate/applications/${applicationId}`;
  },
  getCandidateApplicationCreate: (applicationId: string) => {
    return `/candidate/applications/${applicationId}/create`;
  },
  getCandidateApplicationStart: (applicationId: string) => {
    return publicEnv.NEXT_PUBLIC_RESUME_PARSING_GATE
      ? routes.getCandidateApplicationCreate(applicationId)
      : routes.getCandidateApplicationEdit(applicationId);
  },
  getCandidateApplicationEdit: (applicationId: string, step?: string) => {
    const base = `/candidate/applications/${applicationId}/edit`;

    return step ? `${base}/${step}` : base;
  },
  getCoreApplicationEdit: (jobId: string) => {
    return `/applicant-profile/${jobId}/?ea=t`;
  },
  getCandidateDashboardFromDiscover: (discoverReturnTo: string) => {
    const params = new URLSearchParams({
      application_source: DISCOVER_APPLICATION_SOURCE,
      discover_return_to: discoverReturnTo,
    });

    return `${ATS_CANDIDATE_DASHBOARD_PATH}?${params.toString()}`;
  },
  getAtsAdminDashboard: () => {
    // Note: technically not true but kicking them over to ATS like this avoids user type route juggling
    return ATS_HOME_PATH;
  },
  getQuizDefinitions: () => {
    return "/candidate/quiz/definitions";
  },
  getQuizQuestions: () => {
    return "/candidate/quiz/questions";
  },
  getQuizResults: () => {
    return "/candidate/quiz/results";
  },
  getQuizRequirements: () => {
    return "/candidate/quiz/requirements";
  },
  getPrivacy: () => {
    return "https://www.hirenimble.com/privacy";
  },
  getTerms: () => {
    return "https://www.hirenimble.com/terms";
  },
  getRenewOpenToWork: () => {
    return "/candidate/renew-open-to-work";
  },
};

export default routes;

export function getAbsoluteUrl(url: string): string {
  // If it's already an absolute URL (starts with http:// or https://), return as-is
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  // Convert relative path to absolute URL
  const siteUrl = getSiteUrl();
  // Ensure url starts with /
  const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
  return `${siteUrl}${normalizedUrl}`;
}
